import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Table,
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Snackbar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';

interface SeatAssignment {
  player_id: string;
  seat_number: number;
  playerName?: string; // Added after fetching player details
  profilePictureUrl?: string | null; // Added after fetching player details
}

interface GameSeatAssignment {
  game_number: number;
  seats: SeatAssignment[];
}

interface SeatingPlan {
  id: string;
  league_id: string;
  session_date: string;
  created_at: string;
  games: GameSeatAssignment[];
  leagueName?: string;
  clubName?: string;
  judgeName?: string;
}

const SeatingPlanView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [seatingPlan, setSeatingPlan] = useState<SeatingPlan | null>(null);
  const [statsExpanded, setStatsExpanded] = useState<boolean>(false);
  const [playerStats, setPlayerStats] = useState<{
    playerId: string;
    playerName: string;
    gamesPlayed: number;
    gamesList: number[];
  }[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  useEffect(() => {
    if (id) {
      fetchSeatingPlan(id);
    } else {
      setError('No seating plan ID provided');
      setLoading(false);
    }
  }, [id]);

  const fetchSeatingPlan = async (planId: string) => {
    try {
      // Fetch the seating plan
      const { data, error } = await supabase
        .from('seating_plans')
        .select(`
          id,
          league_id,
          session_date,
          created_at,
          games
        `)
        .eq('id', planId)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('Seating plan not found');
      }

      // Fetch league details
      const { data: leagueData, error: leagueError } = await supabase
        .from('leagues')
        .select(`
          id,
          name,
          club:club_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', data.league_id)
        .single();

      if (leagueError) {
        throw leagueError;
      }

      // Fetch all players involved in the seating plan
      const playerIds = new Set<string>();
      const games = data.games as unknown as GameSeatAssignment[];
      
      games.forEach(game => {
        game.seats.forEach(seat => {
          playerIds.add(seat.player_id);
        });
      });

      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('id, nickname, full_name, profile_picture_url')
        .in('id', Array.from(playerIds));

      if (playersError) {
        throw playersError;
      }

      // Create a player lookup map
      const playerMap = new Map<string, { nickname: string; full_name: string; profile_picture_url?: string | null }>();
      playersData.forEach(player => {
        playerMap.set(player.id, { 
          nickname: player.nickname, 
          full_name: player.full_name,
          profile_picture_url: player.profile_picture_url 
        });
      });

      // Attach player names to the seat assignments
      const enrichedGames = games.map(game => ({
        ...game,
        seats: game.seats.map(seat => ({
          ...seat,
          playerName: playerMap.get(seat.player_id)?.nickname || 'Unknown Player',
          profilePictureUrl: playerMap.get(seat.player_id)?.profile_picture_url
        })).sort((a, b) => a.seat_number - b.seat_number) // Sort by seat number
      }));

      // Create the enriched seating plan
      const enrichedPlan: SeatingPlan = {
        ...data,
        games: enrichedGames,
        leagueName: leagueData?.name || 'Unknown League',
        clubName: leagueData?.club?.name || 'Unknown Club',
        judgeName: (leagueData?.judge as any)?.nickname || 'Unknown Judge'
      };

      setSeatingPlan(enrichedPlan);

      // Generate player statistics
      const stats = Array.from(playerIds).map(playerId => {
        const gamesList: number[] = [];
        games.forEach(game => {
          if (game.seats.some(seat => seat.player_id === playerId)) {
            gamesList.push(game.game_number);
          }
        });

        return {
          playerId,
          playerName: playerMap.get(playerId)?.nickname || 'Unknown Player',
          gamesPlayed: gamesList.length,
          gamesList
        };
      });

      // Sort by name
      stats.sort((a, b) => a.playerName.localeCompare(b.playerName));
      setPlayerStats(stats);

    } catch (err: any) {
      // Check for deleted seating plan errors
      if (err.code === 'PGRST116') {
        setError('Seating arrangements not found or were canceled, ask an admin for the new seating plan');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const copyToClipboard = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Link copied to clipboard',
          severity: 'info'
        });
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to copy link',
          severity: 'error'
        });
      });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Error: {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/leagues')}
          >
            Return to Leagues
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary"
            sx={{ fontWeight: 'bold', m: 0 }}
          >
            Seating Plan
          </Typography>
          <Tooltip title="Copy link to clipboard">
            <IconButton 
              onClick={copyToClipboard}
              color="primary"
              size="medium"
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Seating plan header info */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flexBasis: '100%', flexGrow: 1, minWidth: 0, maxWidth: '100%', '@media (min-width: 900px)': { flexBasis: '48%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmojiEventsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  {seatingPlan?.leagueName}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ ml: 4 }}>
                Club: {seatingPlan?.clubName}
              </Typography>
            </Box>
            <Box sx={{ flexBasis: '100%', flexGrow: 1, minWidth: 0, maxWidth: '100%', '@media (min-width: 900px)': { flexBasis: '48%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  {seatingPlan?.session_date && formatDate(seatingPlan.session_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  Judge: {seatingPlan?.judgeName}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Player participation stats - collapsible */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box 
            onClick={() => setStatsExpanded(!statsExpanded)}
            sx={{ 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom sx={{ mb: 1 }}>
              Seating Stats
            </Typography>
            {statsExpanded ? 
              <ExpandLessIcon color="action" /> : 
              <ExpandMoreIcon color="action" />
            }
          </Box>
          
          {statsExpanded && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Playing in Games</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Missing Games</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {playerStats.map(player => {
                    // Calculate missing games
                    const allGames = seatingPlan?.games.map(g => g.game_number) || [];
                    const missingGames = allGames.filter(gameNum => !player.gamesList.includes(gameNum));
                    
                    return (
                      <TableRow key={player.playerId}>
                        <TableCell>{player.playerName}</TableCell>
                        <TableCell>
                          {player.gamesList.sort((a, b) => a - b).join(', ')}
                        </TableCell>
                        <TableCell>
                          {missingGames.sort((a, b) => a - b).join(', ')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Game-by-game seating assignments */}
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom
          sx={{ fontWeight: 'bold', mt: 4 }}
        >
          Seating
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {seatingPlan?.games.sort((a, b) => a.game_number - b.game_number).map((game) => (
            <Paper key={game.game_number} sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EventSeatIcon sx={{ mr: 1 }} />
                Game #{game.game_number}
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Seat</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {game.seats.map((seat) => (
                      <TableRow key={`${game.game_number}-${seat.player_id}`}>
                        <TableCell>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'bold', 
                              fontSize: '1.2rem', 
                              color: 'text.primary'
                            }}
                          >
                            {seat.seat_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {seat.profilePictureUrl ? (
                              <Box
                                component="img"
                                src={seat.profilePictureUrl}
                                alt={seat.playerName}
                                sx={{ 
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <PersonIcon sx={{ color: 'text.secondary', padding: '2px', background: "#444", borderRadius: "50%", fontSize: "1.2rem" }} />
                            )}
                            {seat.playerName}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SeatingPlanView;